import { useState, useCallback } from 'react';
import { useFetch } from './useFetch';
import API_ROUTES from './apiRoutes';

interface FreeMaterial {
  id: string;
  title: string;
  description: string;
  keywords: string[];
  thumbnailKey: string;
  thumbnailUrl?: string;
  previewUrl: string;
  purchaseUrl: string;
  referenceId?: string;
  templateId?: string;
  contentIds: string[];
  contentCount?: number;
  createdAt?: string;
  isFree: boolean;
}

interface FreeMaterialState {
  materials: FreeMaterial[];
  isLoading: boolean;
  error: string | null;
  success: boolean;
}

export const useFreeMaterials = () => {
  const [materialState, setMaterialState] = useState<FreeMaterialState>({
    materials: [],
    isLoading: false,
    error: null,
    success: false,
  });

  const { fetchWithAuth } = useFetch();

  const fetchFreeMaterials = useCallback(async (): Promise<void> => {
    setMaterialState(prev => ({
      ...prev,
      isLoading: true,
      error: null,
    }));

    try {
      const response = await fetchWithAuth(API_ROUTES.MATERIALS.showcase());

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Erro ao buscar materiais: ${response.status} - ${errorText}`);
      }

      const data = await response.json();

      // A API retorna {materials: Array}, então extraímos o array
      const materialsData = data.materials || [];

      // Filtrar apenas materiais gratuitos (isFree = true)
      const freeMaterialsData = materialsData.filter((material: any) => material.isFree === true);

      // Mapear os campos da API para o formato esperado
      const freeMaterials = freeMaterialsData.map((material: any) => ({
        id: material.id,
        title: material.title,
        description: material.description,
        keywords: material.keywords || [],
        thumbnailKey: material.thumbnailKey,
        thumbnailUrl: material.thumbnailUrl,
        previewUrl: material.previewUrl,
        purchaseUrl: material.purchaseUrl,
        referenceId: material.referenceId,
        templateId: material.templateId,
        contentIds: material.contentIds || [],
        contentCount: material.contents?.length || 0,
        createdAt: material.createdAt,
        isFree: material.isFree,
      }));

      setMaterialState(prev => ({
        ...prev,
        materials: freeMaterials,
        isLoading: false,
      }));
    } catch (error) {
      setMaterialState(prev => ({
        ...prev,
        error: error instanceof Error ? error.message : 'Erro desconhecido',
        isLoading: false,
      }));
    }
  }, [fetchWithAuth]);

  const resetFreeMaterialState = () => {
    setMaterialState({
      materials: [],
      isLoading: false,
      error: null,
      success: false,
    });
  };

  return {
    fetchFreeMaterials,
    materialState,
    resetFreeMaterialState,
  };
};
